import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { setMpin } from "../../services/authServices";
import logger from "../../utils/logger";
import "./onboarding.css";

const PostLoginMpinSetup: React.FC = () => {
  const [mpin, setMpinValue] = useState<string>("");
  const [confirmMpin, setConfirmMpin] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMpin, setShowMpin] = useState<boolean>(false);
  const [showConfirmMpin, setShowConfirmMpin] = useState<boolean>(false);
  const navigate = useNavigate();

  // Check if this is a reset or new setup
  const isReset = localStorage.getItem("mpinSetupType") === "reset";

  // Verify user is authenticated and setup flags are set
  useEffect(() => {
    const authToken = localStorage.getItem("auth_token");
    const showMpinSetup = localStorage.getItem("showMpinSetup");
    const mpinSetupType = localStorage.getItem("mpinSetupType");

    logger.log("[MPIN Setup] Component mounted with state:", {
      hasAuthToken: !!authToken,
      showMpinSetup,
      mpinSetupType,
    });

    // If user is not authenticated or flags aren't set, redirect to login
    if (!authToken || showMpinSetup !== "true" || !mpinSetupType) {
      logger.warn("[MPIN Setup] Missing required state, redirecting to login");
      toast.error("Invalid setup state. Please try again.");
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const handleMpinChange = (value: string, field: 'mpin' | 'confirm') => {
    // Only allow numeric input and limit to 4 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    
    if (field === 'mpin') {
      setMpinValue(numericValue);
    } else {
      setConfirmMpin(numericValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mpin.length !== 4) {
      toast.error("MPIN must be 4 digits");
      return;
    }

    if (mpin !== confirmMpin) {
      toast.error("MPIN and confirmation do not match");
      return;
    }

    setIsLoading(true);

    try {
      // Don't send oldMpin key at all when setting for first time
      await setMpin({ mpin });

      logger.log("[MPIN Setup] PIN successfully set:", {
        setupType: isReset ? 'reset' : 'setup',
        timestamp: new Date().toISOString()
      });

      toast.success(`PIN ${isReset ? 'reset' : 'set'} successfully! You can now use it for quick login.`);

      // Clear the flags and navigate to main app
      localStorage.removeItem("showMpinSetup");
      localStorage.removeItem("mpinSetupType");
      logger.log("[MPIN Setup] Flags cleared, navigating to leads");

      navigate("/leads", { replace: true });
    } catch (error: any) {
      logger.error("[MPIN Setup] Error setting MPIN:", error);
      
      // Extract specific error message from API response
      const errorMessage = error.response?.data?.message || "Failed to set MPIN. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        {/* Logo */}
        <div className="onboarding-logo">
          <img src="assets/img/3sigma_logo.png" alt="3Sigma CRM" />
        </div>

        {/* Header */}
        <div className="onboarding-header">
          <h2>{isReset ? "Reset Your PIN" : "Set Your PIN"}</h2>
          <p className="onboarding-subtitle">
            {isReset ? "Create a new 4-digit PIN for quick login" : "Create a 4-digit PIN for quick login"}
          </p>
        </div>

        {/* Setup Form */}
        <form onSubmit={handleSubmit} className="onboarding-form">
          <div className="onboarding-form-group">
            <label>Enter 4-digit PIN</label>
            <div style={{ position: "relative" }}>
              <input
                type={showMpin ? "text" : "password"}
                className="onboarding-input"
                value={mpin}
                onChange={(e) => handleMpinChange(e.target.value, 'mpin')}
                placeholder="••••"
                maxLength={4}
                autoComplete="off"
                spellCheck="false"
                autoCapitalize="off"
                autoCorrect="off"
                inputMode="numeric"
                pattern="[0-9]*"
                style={{
                  textAlign: "center",
                  letterSpacing: "12px",
                  fontSize: "20px",
                  fontWeight: "700",
                  paddingRight: "45px",
                }}
              />
              <button
                type="button"
                onClick={() => setShowMpin(!showMpin)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  padding: "4px",
                }}
              >
                {showMpin ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <div className="onboarding-form-group">
            <label>Confirm PIN</label>
            <div style={{ position: "relative" }}>
              <input
                type={showConfirmMpin ? "text" : "password"}
                className="onboarding-input"
                value={confirmMpin}
                onChange={(e) => handleMpinChange(e.target.value, 'confirm')}
                placeholder="••••"
                maxLength={4}
                autoComplete="off"
                spellCheck="false"
                autoCapitalize="off"
                autoCorrect="off"
                inputMode="numeric"
                pattern="[0-9]*"
                style={{
                  textAlign: "center",
                  letterSpacing: "12px",
                  fontSize: "20px",
                  fontWeight: "700",
                  paddingRight: "45px",
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmMpin(!showConfirmMpin)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  padding: "4px",
                }}
              >
                {showConfirmMpin ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="onboarding-btn-primary"
            disabled={isLoading || mpin.length !== 4 || confirmMpin.length !== 4}
          >
            {isLoading ? (
              <span className="onboarding-loading">
                <span className="onboarding-spinner"></span>
                {isReset ? "Resetting PIN..." : "Setting PIN..."}
              </span>
            ) : (
              `${isReset ? 'Reset' : 'Set'} PIN`
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PostLoginMpinSetup;